import { useEffect, useRef, useState } from "react";
import { home } from "../content/home";
import { useContent, SlotImg, Rich } from "../ui/helpers";

/*
  "How it works" — the nine-step chain (factory floor → AI-assisted optimisation)
  in the v2 scroll-story layout: a sticky photo on the left changes as each group
  of steps scrolls past on the right. Every step carries a status label
  (symbol + word): available now / shown in the simulation / in development.
  Text: content/home.js → flow
*/
function Status({ status, label }) {
  return (
    <span className={`flow-st fs-${status}`}>
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden="true">
        {status === "available" && <path d="M2 6.2 4.8 9 10 3" fill="none" stroke="currentColor" strokeWidth="1.8" />}
        {status === "demo" && <path d="M3.5 2.5 9.5 6l-6 3.5z" fill="currentColor" />}
        {status === "planned" && <circle cx="6" cy="6" r="4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeDasharray="2 1.6" />}
      </svg>
      {label}
    </span>
  );
}

export function Flow() {
  const c = useContent(home).flow;
  const [active, setActive] = useState(0);
  const stepsRef = useRef(null);

  useEffect(() => {
    const items = [...stepsRef.current.querySelectorAll(".step")];
    const io = new IntersectionObserver(
      (es) => es.forEach((e) => e.isIntersecting && setActive(items.indexOf(e.target))),
      { rootMargin: "-45% 0px -45% 0px" }
    );
    items.forEach((s) => io.observe(s));
    return () => io.disconnect();
  }, []);

  let n = 0;
  return (
    <section className="band-light story flow" id="flow" aria-labelledby="flow-title">
      <div className="k-wrap">
        <div className="story-head rv">
          <div>
            <p className="k-label">{c.eyebrow}</p>
            <h2 id="flow-title" className="h-sec" style={{ marginTop: 16 }}>{c.title}</h2>
          </div>
          <div>
            <p><Rich text={c.lead} /></p>
            <p className="flow-legend">
              {Object.entries(c.legend).map(([k, v]) => <Status key={k} status={k} label={v} />)}
            </p>
          </div>
        </div>
        <div className="story-grid">
          <figure className="story-media">
            {c.groups.map((g, i) => (
              <SlotImg key={g.media} slot={g.media} className={i === active ? "on" : undefined} sizes="(min-width: 860px) 55vw, 100vw" />
            ))}
            <figcaption>{c.groups[active].caption}</figcaption>
            <div className="story-dots" aria-hidden="true">{c.groups.map((g, i) => <i key={g.name} className={i === active ? "on" : undefined} />)}</div>
          </figure>
          <ol className="steps js" ref={stepsRef}>
            {c.groups.map((g, i) => (
              <li key={g.name} className={`step${i === active ? " on" : ""}`}>
                <p className="k-label">{String(i + 1).padStart(2, "0")} <i>/ {g.name}</i></p>
                <ol className="flow-stops">
                  {g.stops.map((s) => {
                    n += 1;
                    return (
                      <li key={s.title} className={`stop st-${s.status}`}>
                        <span className="stop-n" aria-hidden="true">{n}</span>
                        <div>
                          <h3>{s.title}{s.product && <span className="prod">{s.product}</span>}</h3>
                          <p><Rich text={s.body} /></p>
                          <Status status={s.status} label={c.legend[s.status]} />
                        </div>
                      </li>
                    );
                  })}
                </ol>
                <SlotImg slot={g.media} className="step-img" sizes="100vw" />
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
